import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Image, Modal, Space, Tooltip} from 'antd';
import {
    AudioOutlined,
    DownloadOutlined,
    ExportOutlined,
    FileExcelOutlined,
    FileOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    FileWordOutlined,
    FileZipOutlined,
    PlayCircleOutlined,
    VideoCameraOutlined,
} from '@ant-design/icons';

/**
 * 申诉/审核凭证附件的统一格子样式展示。
 *
 * 设计目标：
 *   - 多类型附件混排时视觉权重一致：图片 / 音频 / 视频 / 文档 都按 96×96 卡片渲染
 *   - 文件名超长截断，悬浮 tooltip 展示完整名，避免页面错位
 *   - 音视频不在列表里直接铺播放器（会占大面积），而是图标占位 + 点击 Modal 内播放
 *   - 不依赖第三方库；类型识别仅按扩展名（业务 mock / 真实 S3 key 都满足）
 *
 * 类型分流：
 *   - 图片（jpg/png/gif/webp/bmp） → antd Image 缩略图（外层 Image.PreviewGroup 接管点击放大）
 *   - 音频（mp3/wav/m4a/ogg/aac/flac） → 喇叭图标 + 文件名；点击在 Modal 内 HTML5 <audio>
 *   - 视频（mp4/webm/mov/m4v） → 播放按钮 + 文件名；点击在 Modal 内 HTML5 <video>；
 *     注意：iOS 录制的 .mov 多为 HEVC/H.265，Chrome 桌面无法解码会黑屏，是浏览器限制；
 *     用户可右键"在新标签打开"由系统播放器接管
 *   - 其它（pdf/doc/xlsx/zip/txt 等） → 类型图标 + 文件名链接，新标签打开（pdf 内置 viewer / 其它触发下载）
 */
const TILE_SIZE = 96;
const NAME_MAX_CHARS = 14; // 文件名超此截断尾部并加 …

function extractFileName(url: string): string {
    try {
        const pathname = new URL(url).pathname;
        return decodeURIComponent(pathname.split('/').pop() || url);
    } catch {
        return url;
    }
}

function truncateMiddle(name: string, max: number): string {
    if (name.length <= max) return name;
    const head = name.slice(0, Math.ceil((max - 1) / 2));
    const tail = name.slice(-Math.floor((max - 1) / 2));
    return `${head}…${tail}`;
}

type AttachmentKind = 'image' | 'audio' | 'video' | 'pdf' | 'word' | 'excel' | 'zip' | 'text' | 'other';

function detectKind(fileName: string): AttachmentKind {
    const lower = fileName.toLowerCase();
    if (/\.(png|jpe?g|gif|webp|bmp)$/.test(lower)) return 'image';
    if (/\.(mp3|wav|m4a|ogg|aac|flac)$/.test(lower)) return 'audio';
    if (/\.(mp4|webm|mov|m4v)$/.test(lower)) return 'video';
    if (/\.pdf$/.test(lower)) return 'pdf';
    if (/\.(docx?)$/.test(lower)) return 'word';
    if (/\.(xlsx?|csv)$/.test(lower)) return 'excel';
    if (/\.(zip|rar|7z|tar|gz)$/.test(lower)) return 'zip';
    if (/\.(txt|log|md)$/.test(lower)) return 'text';
    return 'other';
}

const KIND_ICON: Record<Exclude<AttachmentKind, 'image'>, React.ReactNode> = {
    audio: <AudioOutlined style={{fontSize: 36, color: '#1677ff'}} />,
    video: <VideoCameraOutlined style={{fontSize: 36, color: '#722ed1'}} />,
    pdf: <FilePdfOutlined style={{fontSize: 36, color: '#ff4d4f'}} />,
    word: <FileWordOutlined style={{fontSize: 36, color: '#1677ff'}} />,
    excel: <FileExcelOutlined style={{fontSize: 36, color: '#52c41a'}} />,
    zip: <FileZipOutlined style={{fontSize: 36, color: '#faad14'}} />,
    text: <FileTextOutlined style={{fontSize: 36, color: '#595959'}} />,
    other: <FileOutlined style={{fontSize: 36, color: '#8c8c8c'}} />,
};

interface AttachmentTileProps {
    url: string;
}

const AttachmentTile: React.FC<AttachmentTileProps> = ({url}) => {
    const {t} = useTranslation();
    const [playerOpen, setPlayerOpen] = useState(false);
    const fileName = extractFileName(url);
    const kind = detectKind(fileName);
    const display = truncateMiddle(fileName, NAME_MAX_CHARS);
    const tileStyle: React.CSSProperties = {
        width: TILE_SIZE,
        // 整体高度 = 图标区 + 文件名区
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
    };

    // 图片：外层可能包了 Image.PreviewGroup，保留原 antd Image 行为
    if (kind === 'image') {
        return (
            <div style={tileStyle}>
                <Image
                    src={url}
                    width={TILE_SIZE}
                    height={TILE_SIZE}
                    style={{objectFit: 'cover', borderRadius: 6, border: '1px solid #f0f0f0'}}
                    fallback="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI5NiIgaGVpZ2h0PSI5NiI+PHJlY3Qgd2lkdGg9Ijk2IiBoZWlnaHQ9Ijk2IiBmaWxsPSIjZjVmNWY1Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiNiZmJmYmYiIGZvbnQtc2l6ZT0iMTIiPuaXoOWbvueJhzwvdGV4dD48L3N2Zz4="
                />
                <Tooltip title={fileName}>
                    <div style={{
                        width: TILE_SIZE, fontSize: 12, color: '#595959',
                        overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', textAlign: 'center',
                    }}>{display}</div>
                </Tooltip>
            </div>
        );
    }

    // 音频/视频：缩略卡 + 播放按钮叠加；点击打开 Modal 播放
    if (kind === 'audio' || kind === 'video') {
        // 部分编码（如 iOS 录制 .mov 默认为 HEVC/H.265）在 Chrome 桌面无法解码，
        // 提供"在新标签打开"和"下载"两条兜底路径，不依赖浏览器是否真的能播放
        const fallbackHint = kind === 'video'
            ? t('common.attachment.playbackFallbackHintVideo')
            : t('common.attachment.playbackFallbackHintAudio');
        return (
            <>
                <div style={tileStyle}>
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setPlayerOpen(true)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') setPlayerOpen(true);
                        }}
                        style={{
                            width: TILE_SIZE, height: TILE_SIZE,
                            background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            position: 'relative', cursor: 'pointer',
                        }}
                    >
                        {KIND_ICON[kind]}
                        <PlayCircleOutlined
                            style={{
                                position: 'absolute', right: 4, bottom: 4,
                                fontSize: 18, color: '#1677ff', background: '#fff', borderRadius: '50%',
                            }}
                        />
                    </div>
                    <Tooltip title={fileName}>
                        <div style={{
                            width: TILE_SIZE, fontSize: 12, color: '#595959',
                            overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', textAlign: 'center',
                        }}>{display}</div>
                    </Tooltip>
                </div>
                <Modal
                    open={playerOpen}
                    onCancel={() => setPlayerOpen(false)}
                    footer={
                        <Space>
                            <Button icon={<ExportOutlined />} onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>
                                {t('common.buttons.openInNewTab')}
                            </Button>
                            <Button icon={<DownloadOutlined />} href={url} download={fileName} target="_blank">
                                {t('common.buttons.download')}
                            </Button>
                        </Space>
                    }
                    title={fileName}
                    destroyOnClose
                    width={kind === 'video' ? 640 : 420}
                >
                    {kind === 'audio' ? (
                        <audio controls autoPlay preload="metadata" src={url} style={{width: '100%'}} />
                    ) : (
                        <video
                            controls
                            autoPlay
                            preload="metadata"
                            src={url}
                            style={{width: '100%', maxHeight: 480, background: '#000'}}
                        />
                    )}
                    <div style={{marginTop: 12, fontSize: 12, color: '#8c8c8c'}}>
                        {fallbackHint}
                    </div>
                </Modal>
            </>
        );
    }

    // 文档/压缩包等：图标 + 文件名超链接（新标签打开/下载）
    return (
        <Tooltip title={fileName}>
            <a
                href={url}
                target="_blank"
                rel="noreferrer"
                style={{
                    ...tileStyle,
                    textDecoration: 'none',
                }}
            >
                <div style={{
                    width: TILE_SIZE, height: TILE_SIZE,
                    background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {KIND_ICON[kind]}
                </div>
                <div style={{
                    width: TILE_SIZE, fontSize: 12, color: '#1677ff',
                    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', textAlign: 'center',
                }}>{display}</div>
            </a>
        </Tooltip>
    );
};

export default AttachmentTile;
